import { get } from 'lodash';
import {
  AiAppUserHistoryStateKey,
  EolsUserHistoryResponseDto,
  WelcomeDisclaimerStateInfoDto
} from '../../apis/eols-user-crud/eols-user-crud.dtos';
import { Brand } from '../../constants/common.constants';
import { WelcomeModalConfig } from './welcome-disclaimer.config';
import { NON_COURSE_SCOPED_SECTION_ID } from './welcome-disclaimer-modal.constants';
import {
  fetchUserHistoryByCourseByStateKeyAction,
  postUserHistoryAction,
  trackNewRelicAction
} from '../app/app-state-actions.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { AppContextState } from '../app/AppProvider.component';

export type WelcomeDisclaimerRecord = {
  historyId: number;
  stateInfo: WelcomeDisclaimerStateInfoDto;
}

export const shouldShowDisclaimer = (
  stored: WelcomeDisclaimerStateInfoDto | null,
  config: WelcomeModalConfig
): boolean => {
  if (!stored) {
    return true;
  }
  return stored.version < config.version;
};

const parseStateInfo = (raw: string): WelcomeDisclaimerStateInfoDto | null => {
  try {
    const parsed = JSON.parse(raw) as Partial<WelcomeDisclaimerStateInfoDto>;
    if (!parsed || typeof parsed.version !== 'number' || !parsed.brand) {
      return null;
    }
    return parsed as WelcomeDisclaimerStateInfoDto;
  } catch {
    return null;
  }
};

const parseRecord = (record: EolsUserHistoryResponseDto): WelcomeDisclaimerRecord | null => {
  const stateInfo = parseStateInfo(record.stateInfo);
  if (!stateInfo) {
    return null;
  }
  return { historyId: record.id, stateInfo };
};

const fetchRawRecords = (props: AppContextState & {
  userId: number;
}): Promise<EolsUserHistoryResponseDto[]> => {
  return fetchUserHistoryByCourseByStateKeyAction({
    appState: props.appState,
    setAppState: props.setAppState,
    appStateRef: props.appStateRef,
    userId: props.userId,
    stateKey: AiAppUserHistoryStateKey.WELCOME_DISCLAIMER_ACCEPTED,
    courseSectionId: NON_COURSE_SCOPED_SECTION_ID,
  }).catch((error) => {
    // No acceptance record yet (first launch) returns 404 - treat as "not
    // accepted" rather than an error so the modal shows via the normal path.
    if (get(error, 'response.status') === 404) {
      return [];
    }
    throw error;
  });
};

export const fetchLatestAcceptancePerBrand = async (props: AppContextState & {
  userId: number;
}): Promise<Partial<Record<Brand, WelcomeDisclaimerRecord>>> => {
  const records = await fetchRawRecords(props);
  if (!records || !records.length) {
    return {};
  }

  const parsed = records
    .map(parseRecord)
    .filter((r): r is WelcomeDisclaimerRecord => r !== null);

  const latestByBrand: Partial<Record<Brand, WelcomeDisclaimerRecord>> = {};
  parsed.forEach((record) => {
    const existing = latestByBrand[record.stateInfo.brand];
    if (!existing || record.historyId > existing.historyId) {
      latestByBrand[record.stateInfo.brand] = record;
    }
  });
  return latestByBrand;
};

export const fetchAcceptance = async (props: AppContextState & {
  userId: number;
  brand: Brand;
}): Promise<WelcomeDisclaimerStateInfoDto | null> => {
  const latestByBrand = await fetchLatestAcceptancePerBrand(props);
  const record = latestByBrand[props.brand];
  return record ? record.stateInfo : null;
};

export const resetAcceptance = (props: AppContextState & {
  userId: number;
  brand: Brand;
}): Promise<unknown> => {
  const stateInfo: WelcomeDisclaimerStateInfoDto = {
    brand: props.brand,
    version: 0,
    acceptedAt: new Date().toISOString(),
  };

  return postUserHistoryAction({
    appState: props.appState,
    setAppState: props.setAppState,
    appStateRef: props.appStateRef,
    eolsUserHistoryDTO: {
      stateKey: AiAppUserHistoryStateKey.WELCOME_DISCLAIMER_ACCEPTED,
      stateInfo: JSON.stringify(stateInfo),
      courseSectionId: NON_COURSE_SCOPED_SECTION_ID,
      eolsUser: {
        id: props.userId,
      },
    },
  });
};

export const recordAcceptance = async (props: AppContextState & {
  userId: number;
  brand: Brand;
  version: number;
}): Promise<unknown> => {
  const stateInfo: WelcomeDisclaimerStateInfoDto = {
    brand: props.brand,
    version: props.version,
    acceptedAt: new Date().toISOString(),
  };

  const response = await postUserHistoryAction({
    appState: props.appState,
    setAppState: props.setAppState,
    appStateRef: props.appStateRef,
    eolsUserHistoryDTO: {
      stateKey: AiAppUserHistoryStateKey.WELCOME_DISCLAIMER_ACCEPTED,
      stateInfo: JSON.stringify(stateInfo),
      courseSectionId: NON_COURSE_SCOPED_SECTION_ID,
      eolsUser: {
        id: props.userId,
      },
    },
  });

  trackNewRelicAction({
    appStateRef: props.appStateRef,
    actionProps: {
      action: NewRelicAnalyticsAction.WELCOME_DISCLAIMER_ACCEPTED,
      props: {
        brand: props.brand,
        version: props.version,
      },
    },
  });

  return response;
};
